import { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import {
  User,
  Settings,
  Shield,
  Palette,
  Save,
  Moon,
  Sun,
  Camera,
  Loader2,
  Languages,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useDashboardTheme } from '../context/DashboardThemeContext';
import { usePreferredCurrencyOptional } from '../context/CurrencySettingsContext';

type ProfileRow = {
  id: string;
  first_name: string | null;
  last_name: string | null;
  username: string | null;
  avatar_url: string | null;
};

export function DashboardSettings() {
  const { t, refreshDefaultLanguage } = useLanguage();
  const { user } = useAuth();
  const { theme, setTheme } = useDashboardTheme();
  const currencyContext = usePreferredCurrencyOptional();
  const refreshPreferredCurrency = currencyContext?.refreshPreferredCurrency;

  const [profile, setProfile] = useState<ProfileRow | null>(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileForm, setProfileForm] = useState({
    first_name: '',
    last_name: '',
    username: '',
    avatar_url: '',
  });
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [preferredCurrency, setPreferredCurrency] = useState<'XOF' | 'EUR'>('XOF');
  const [defaultSiteLanguage, setDefaultSiteLanguage] = useState<'fr' | 'en'>('fr');
  const [siteLanguageSettingsId, setSiteLanguageSettingsId] = useState<string | null>(null);
  const [currencyLoading, setCurrencyLoading] = useState(true);
  const [currencySaving, setCurrencySaving] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  const [passwordForm, setPasswordForm] = useState({
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const email = (user as { email?: string } | null)?.email ?? '';

  useEffect(() => {
    if (!user?.id) {
      setProfileLoading(false);
      return;
    }
    let cancelled = false;
    supabase
      .from('profiles')
      .select('id, first_name, last_name, username, avatar_url, role')
      .eq('id', user.id)
      .maybeSingle()
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          setProfileLoading(false);
          return;
        }
        const row = data as (ProfileRow & { role?: string }) | null;
        setProfile(row ?? null);
        setIsAdmin(row?.role === 'admin');
        if (row) {
          setProfileForm({
            first_name: row.first_name ?? '',
            last_name: row.last_name ?? '',
            username: row.username ?? '',
            avatar_url: row.avatar_url ?? '',
          });
        }
        setProfileLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      supabase.from('settings').select('value').eq('key', 'preferred_currency').maybeSingle(),
      supabase.from('site_language_settings').select('id, default_language').limit(1).maybeSingle(),
    ]).then(([currencyRes, langRes]) => {
      if (cancelled) return;
      if (!currencyRes.error && currencyRes.data?.value) {
        const v = (currencyRes.data.value as string).toUpperCase();
        setPreferredCurrency(v === 'EUR' ? 'EUR' : 'XOF');
      }
      if (!langRes.error && langRes.data) {
        const row = langRes.data as { id: string; default_language: string };
        setSiteLanguageSettingsId(row.id);
        setDefaultSiteLanguage((row.default_language === 'en' ? 'en' : 'fr') as 'fr' | 'en');
      }
      setCurrencyLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const saveProfile = async () => {
    if (!user?.id) return;
    setProfileSaving(true);
    const payload = {
      first_name: profileForm.first_name || null,
      last_name: profileForm.last_name || null,
      username: profileForm.username || null,
      avatar_url: profileForm.avatar_url || null,
      updated_at: new Date().toISOString(),
    };
    if (profile) {
      await supabase.from('profiles').update(payload).eq('id', user.id);
    } else {
      await supabase.from('profiles').insert({
        id: user.id,
        ...payload,
      });
    }
    setProfileSaving(false);
  };

  const uploadAvatar = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user?.id) return;
    if (!file.type.startsWith('image/')) {
      setAvatarError('Veuillez choisir une image (JPG, PNG, etc.).');
      return;
    }
    setAvatarError(null);
    setAvatarUploading(true);
    const ext = file.name.split('.').pop() || 'jpg';
    const path = `${user.id}/avatar.${ext}`;
    const { error: uploadError } = await supabase.storage
      .from('avatars')
      .upload(path, file, { upsert: true });
    if (uploadError) {
      setAvatarError(uploadError.message || "Erreur lors de l'envoi.");
      setAvatarUploading(false);
      e.target.value = '';
      return;
    }
    const { data: urlData } = supabase.storage.from('avatars').getPublicUrl(path);
    setProfileForm((p) => ({ ...p, avatar_url: urlData.publicUrl }));
    await supabase
      .from('profiles')
      .update({ avatar_url: urlData.publicUrl, updated_at: new Date().toISOString() })
      .eq('id', user.id);
    setAvatarUploading(false);
    e.target.value = '';
  };

  const saveSiteSettings = async () => {
    setCurrencySaving(true);
    const { error: currencyError } = await supabase
      .from('settings')
      .upsert(
        { key: 'preferred_currency', value: preferredCurrency, updated_at: new Date().toISOString() },
        { onConflict: 'key' }
      );
    if (currencyError) {
      console.error('Erreur enregistrement devise:', currencyError);
    } else if (refreshPreferredCurrency) {
      await refreshPreferredCurrency();
    }
    if (siteLanguageSettingsId) {
      const { error: langError } = await supabase
        .from('site_language_settings')
        .update({
          default_language: defaultSiteLanguage,
          updated_at: new Date().toISOString(),
        })
        .eq('id', siteLanguageSettingsId);
      if (!langError) {
        await refreshDefaultLanguage();
      } else {
        console.error('Erreur enregistrement langue du site:', langError);
      }
    }
    setCurrencySaving(false);
  };

  const savePassword = async () => {
    setPasswordMessage(null);
    if (passwordForm.newPassword.length < 6) {
      setPasswordMessage({ type: 'error', text: 'Le mot de passe doit contenir au moins 6 caractères.' });
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordMessage({ type: 'error', text: 'Les deux mots de passe ne correspondent pas.' });
      return;
    }
    setPasswordSaving(true);
    const { error } = await supabase.auth.updateUser({ password: passwordForm.newPassword });
    setPasswordSaving(false);
    if (error) {
      setPasswordMessage({ type: 'error', text: error.message });
      return;
    }
    setPasswordMessage({ type: 'success', text: 'Mot de passe mis à jour.' });
    setPasswordForm({ newPassword: '', confirmPassword: '' });
  };

  return (
    <div className="flex flex-1 flex-col gap-6">
      <div className="rounded-3xl border border-border/80 bg-card p-6 shadow-[0px_20px_40px_rgba(0,0,0,0.06)] dark:shadow-none md:p-8">
        <p className="flex items-center gap-2 font-['Rubik'] text-sm font-medium text-muted-foreground">
          <Settings className="size-4" />
          {t('business.settings')}
        </p>
        <h2 className="mt-1 font-['Sora'] text-2xl font-semibold tracking-tight text-foreground md:text-3xl">
          Paramètres
        </h2>
        <p className="mt-1 font-['Rubik'] text-sm text-muted-foreground">
          Gérez votre profil et les préférences du compte.
        </p>
      </div>

      <Tabs defaultValue="profile" className="w-full">
        <TabsList className="w-fit flex-wrap justify-start gap-1 rounded-2xl border border-border/60 bg-muted/50 p-1.5 group-data-horizontal/tabs:h-12">
          <TabsTrigger value="profile" className="gap-2 rounded-xl px-4">
            <User className="size-4" />
            Profil personnel
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="site" className="gap-2 rounded-xl px-4">
              <Settings className="size-4" />
              Paramètres site
            </TabsTrigger>
          )}
          <TabsTrigger value="security" className="gap-2 rounded-xl px-4">
            <Shield className="size-4" />
            Sécurité
          </TabsTrigger>
          <TabsTrigger value="appearance" className="gap-2 rounded-xl px-4">
            <Palette className="size-4" />
            Apparence
          </TabsTrigger>
        </TabsList>

        <TabsContent value="profile">
          <Card className="overflow-hidden rounded-2xl border border-border/80 bg-card shadow-[0px_20px_40px_rgba(0,0,0,0.06)] dark:shadow-none">
            <CardHeader className="border-b border-border/60">
              <CardTitle className="text-foreground">Profil personnel</CardTitle>
              <CardDescription className="text-muted-foreground">
                Informations de votre compte.
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              {profileLoading ? (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="size-5 animate-spin" />
                  Chargement…
                </div>
              ) : (
                <div className="grid gap-6 sm:max-w-md">
                  <div className="flex flex-col gap-3">
                    <Label className="text-foreground">Photo de profil</Label>
                    <div className="flex items-center gap-4">
                      <div className="relative flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-border bg-muted/50">
                        {profileForm.avatar_url ? (
                          <img
                            src={profileForm.avatar_url}
                            alt="Photo de profil"
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <User className="size-12 text-muted-foreground" />
                        )}
                        {avatarUploading && (
                          <div className="absolute inset-0 flex items-center justify-center bg-background/80">
                            <Loader2 className="size-8 animate-spin text-primary" />
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col gap-2">
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={uploadAvatar}
                          aria-label="Choisir une photo"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="w-fit gap-2 rounded-xl"
                          disabled={avatarUploading}
                          onClick={() => fileInputRef.current?.click()}
                        >
                          {avatarUploading ? (
                            <Loader2 className="size-4 animate-spin" />
                          ) : (
                            <Camera className="size-4" />
                          )}
                          Changer la photo
                        </Button>
                        {avatarError && <p className="text-sm text-red-400">{avatarError}</p>}
                      </div>
                    </div>
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="profile-first-name" className="text-foreground">Prénom</Label>
                      <Input
                        id="profile-first-name"
                        placeholder="Prénom"
                        value={profileForm.first_name}
                        onChange={(e) => setProfileForm((p) => ({ ...p, first_name: e.target.value }))}
                        className="rounded-xl"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="profile-last-name" className="text-foreground">Nom</Label>
                      <Input
                        id="profile-last-name"
                        placeholder="Nom"
                        value={profileForm.last_name}
                        onChange={(e) => setProfileForm((p) => ({ ...p, last_name: e.target.value }))}
                        className="rounded-xl"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-foreground">Email</Label>
                    <Input
                      value={email || '—'}
                      readOnly
                      disabled
                      className="rounded-xl bg-muted/50"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="profile-username" className="text-foreground">Nom d'utilisateur</Label>
                    <Input
                      id="profile-username"
                      placeholder="username"
                      value={profileForm.username}
                      onChange={(e) => setProfileForm((p) => ({ ...p, username: e.target.value }))}
                      className="rounded-xl"
                    />
                  </div>
                  <Button
                    className="w-fit gap-2 rounded-xl bg-white text-neutral-950 hover:bg-white/90"
                    size="sm"
                    onClick={saveProfile}
                    disabled={profileSaving}
                  >
                    {profileSaving ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      <Save className="size-4" />
                    )}
                    Enregistrer
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {isAdmin && (
          <TabsContent value="site">
            <Card className="overflow-hidden rounded-2xl border border-border/80 bg-card shadow-[0px_20px_40px_rgba(0,0,0,0.06)] dark:shadow-none">
              <CardHeader className="border-b border-border/60">
                <CardTitle className="text-foreground">Paramètres du site</CardTitle>
                <CardDescription className="text-muted-foreground">
                  Devise et langue par défaut pour la boutique (admin).
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                {currencyLoading ? (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="size-5 animate-spin" />
                    Chargement…
                  </div>
                ) : (
                  <div className="grid gap-6 sm:max-w-md">
                    <div className="space-y-2">
                      <Label htmlFor="site-currency" className="text-foreground">Devise préférée</Label>
                      <select
                        id="site-currency"
                        aria-label="Devise préférée"
                        value={preferredCurrency}
                        onChange={(e) => setPreferredCurrency(e.target.value as 'XOF' | 'EUR')}
                        className="h-8 w-full rounded-xl border border-input bg-background px-2.5 py-1 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                      >
                        <option value="XOF">XOF (Franc CFA)</option>
                        <option value="EUR">EUR (Euro)</option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="site-default-lang" className="text-foreground flex items-center gap-2">
                        <Languages className="size-4" />
                        Langue par défaut du site
                      </Label>
                      <select
                        id="site-default-lang"
                        aria-label="Langue par défaut du site"
                        value={defaultSiteLanguage}
                        onChange={(e) => setDefaultSiteLanguage(e.target.value as 'fr' | 'en')}
                        className="h-8 w-full rounded-xl border border-input bg-background px-2.5 py-1 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                      >
                        <option value="fr">Français</option>
                        <option value="en">English</option>
                      </select>
                      <p className="text-xs text-muted-foreground">
                        Langue affichée par défaut pour les visiteurs du site.
                      </p>
                    </div>
                    <Button
                      className="w-fit gap-2 rounded-xl"
                      size="sm"
                      onClick={saveSiteSettings}
                      disabled={currencySaving}
                    >
                      {currencySaving ? (
                        <Loader2 className="size-4 animate-spin" />
                      ) : (
                        <Save className="size-4" />
                      )}
                      Enregistrer
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        <TabsContent value="security">
          <Card className="overflow-hidden rounded-2xl border border-border/80 bg-card shadow-[0px_20px_40px_rgba(0,0,0,0.06)] dark:shadow-none">
            <CardHeader className="border-b border-border/60">
              <CardTitle className="text-foreground">Mot de passe</CardTitle>
              <CardDescription className="text-muted-foreground">
                Modifiez votre mot de passe de connexion.
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid gap-6 sm:max-w-md">
                <div className="space-y-2">
                  <Label htmlFor="new-password" className="text-foreground">Nouveau mot de passe</Label>
                  <Input
                    id="new-password"
                    type="password"
                    placeholder="••••••••"
                    value={passwordForm.newPassword}
                    onChange={(e) => setPasswordForm((p) => ({ ...p, newPassword: e.target.value }))}
                    className="rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-password" className="text-foreground">Confirmer le mot de passe</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    placeholder="••••••••"
                    value={passwordForm.confirmPassword}
                    onChange={(e) => setPasswordForm((p) => ({ ...p, confirmPassword: e.target.value }))}
                    className="rounded-xl"
                  />
                </div>
                {passwordMessage && (
                  <p
                    className={cn(
                      'text-sm',
                      passwordMessage.type === 'success' ? 'text-emerald-400' : 'text-red-400'
                    )}
                  >
                    {passwordMessage.text}
                  </p>
                )}
                <Button
                  className="w-fit gap-2 rounded-xl"
                  size="sm"
                  onClick={savePassword}
                  disabled={passwordSaving}
                >
                  {passwordSaving ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <Save className="size-4" />
                  )}
                  Changer le mot de passe
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="appearance">
          <Card className="overflow-hidden rounded-2xl border border-border/80 bg-card shadow-[0px_20px_40px_rgba(0,0,0,0.06)] dark:shadow-none">
            <CardHeader className="border-b border-border/60">
              <CardTitle className="text-foreground">Apparence du dashboard</CardTitle>
              <CardDescription className="text-muted-foreground">
                Thème d'affichage de l'espace admin uniquement (le site public ne change pas).
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => setTheme('light')}
                  className={cn(
                    'flex items-center gap-3 rounded-2xl border-2 px-4 py-3 transition-colors',
                    theme === 'light'
                      ? 'border-primary bg-primary/10 text-foreground'
                      : 'border-border/60 bg-muted/20 text-muted-foreground hover:border-border hover:bg-muted/40 hover:text-foreground'
                  )}
                >
                  <Sun className="size-5" />
                  <span className="font-medium">Clair</span>
                </button>
                <button
                  type="button"
                  onClick={() => setTheme('dark')}
                  className={cn(
                    'flex items-center gap-3 rounded-2xl border-2 px-4 py-3 transition-colors',
                    theme === 'dark'
                      ? 'border-primary bg-primary/10 text-foreground'
                      : 'border-border/60 bg-muted/20 text-muted-foreground hover:border-border hover:bg-muted/40 hover:text-foreground'
                  )}
                >
                  <Moon className="size-5" />
                  <span className="font-medium">Sombre</span>
                </button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
